import { useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { message } from 'antd';
import { useStateContext } from '../../../../../contexts/ContextProvider';

export const useProductAnalyzer = () => {
  const { token } = useStateContext();
  const [analyzing, setAnalyzing] = useState(false);
  const [progress, setProgress] = useState('');
  const pollingInterval = useRef(null);

  const clearPolling = useCallback(() => {
    if (pollingInterval.current) {
      clearInterval(pollingInterval.current);
      pollingInterval.current = null;
    }
  }, []);

  const pollJobStatus = useCallback(async (jobId) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/produse/analyze-product-url/${jobId}`,
        { params: { token } }
      );
      
      const jobData = response.data;
      
      switch (jobData.status) {
        case 'processing':
          setProgress('Se analizează conținutul...');
          break;
        case 'completed':
          clearPolling();
          setAnalyzing(false);
          setProgress('');
          message.success('Produsul a fost analizat cu succes');
          return jobData;
        case 'failed':
          clearPolling();
          setAnalyzing(false);
          setProgress('');
          message.error(jobData.error || 'Eroare la analizarea produsului');
          return null;
        default:
          setProgress('Se procesează cererea...');
      }
    } catch (error) {
      console.error('Eroare la verificarea statusului:', error);
      clearPolling();
      setAnalyzing(false);
      setProgress('');
      message.error('Eroare la verificarea statusului analizei');
      return null;
    }
    return 'polling';
  }, [token, clearPolling]);

  const analyzeProductFromUrl = useCallback(async (url) => {
    if (!token || !url) return null;
    
    setAnalyzing(true);
    setProgress('Se inițiază analiza...');
    
    try {
      // Inițiem analiza
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/produse/analyze-product-url`, {
        url: url.trim()
      }, {
        params: { token }
      });
      
      const { job_id } = response.data;
      setProgress('Analiza a început...');
      
      // Începem polling-ul pentru status
      return new Promise((resolve) => {
        pollingInterval.current = setInterval(async () => {
          const result = await pollJobStatus(job_id);
          if (result !== 'polling') {
            resolve(result);
          }
        }, 2000); // Verificăm la fiecare 2 secunde
        
        // Timeout după 5 minute
        setTimeout(() => {
          clearPolling();
          setAnalyzing(false);
          setProgress('');
          message.error('Timeout - analiza a durat prea mult');
          resolve(null);
        }, 300000);
      });
      
    } catch (error) {
      console.error('Eroare la analizarea URL-ului:', error);
      setAnalyzing(false);
      setProgress('');
      message.error('Eroare la inițierea analizei produsului');
      return null;
    }
  }, [token, pollJobStatus, clearPolling]);

  // Cleanup la unmount
  const cleanup = useCallback(() => {
    clearPolling();
    setAnalyzing(false);
    setProgress('');
  }, [clearPolling]);

  return {
    analyzeProductFromUrl,
    analyzing,
    progress,
    cleanup
  };
}; 